import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { clientApi } from '@/api/client'
import { clearSession, getSessionToken, setActiveOrgId, setDefaultWatchlistId, setSessionToken } from '@/auth/session'
import ErrorBox from '@/components/ErrorBox'
import Topbar from '@/components/Topbar'

const resendStateStorageKey = 'cenva_intel_auth_resend_state'
const resendCooldownMs = 60_000

export default function Login() {
  const nav = useNavigate()
  const inviteStorageKey = 'cenva_intel_invite_code'
  const [email, setEmail] = useState('')
  const [token, setToken] = useState('')
  const [devToken, setDevToken] = useState<string | null>(null)
  const [inviteCode, setInviteCode] = useState<string | null>(null)
  const [invitePreview, setInvitePreview] = useState<any | null>(null)
  const [orgName, setOrgName] = useState('')
  const [busyAction, setBusyAction] = useState<'sending' | 'verifying' | 'joining' | null>(null)
  const [error, setError] = useState<any>(null)
  const [lastRequestedEmail, setLastRequestedEmail] = useState('')
  const [resendUntil, setResendUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null)
  const [inviteConflict, setInviteConflict] = useState(false)
  const [bootstrapping, setBootstrapping] = useState(true)

  const normalizedEmail = email.trim().toLowerCase()
  const resendAppliesToCurrentEmail = !!normalizedEmail && normalizedEmail === lastRequestedEmail
  const resendSeconds = resendAppliesToCurrentEmail ? Math.max(0, Math.ceil((resendUntil - now) / 1000)) : 0
  const resendLocked = resendSeconds > 0
  const busy = busyAction !== null

  useEffect(() => {
    let cancelled = false

    async function init() {
      let nextInviteCode: string | null = null

      try {
        const params = new URLSearchParams(window.location.search)
        const inv = (params.get('invite') || '').trim()
        if (inv) {
          nextInviteCode = inv
          setInviteCode(inv)
          localStorage.setItem(inviteStorageKey, inv)
          params.delete('invite')
          const qs = params.toString()
          const nextUrl = window.location.pathname + (qs ? `?${qs}` : '') + (window.location.hash || '')
          window.history.replaceState({}, document.title, nextUrl)
        } else {
          const stored = (localStorage.getItem(inviteStorageKey) || '').trim()
          if (stored) {
            nextInviteCode = stored
            setInviteCode(stored)
          }
        }
      } catch {
        // ignore
      }

      try {
        const resendRaw = localStorage.getItem(resendStateStorageKey)
        if (resendRaw) {
          const parsed = JSON.parse(resendRaw) as { email?: string; until?: number }
          const storedEmail = String(parsed?.email || '').trim().toLowerCase()
          const storedUntil = Number(parsed?.until || 0)
          if (storedEmail && Number.isFinite(storedUntil) && storedUntil > Date.now()) {
            setLastRequestedEmail(storedEmail)
            setResendUntil(storedUntil)
          } else {
            localStorage.removeItem(resendStateStorageKey)
          }
        }
      } catch {
        try {
          localStorage.removeItem(resendStateStorageKey)
        } catch {
          // ignore
        }
      }

      const existingToken = getSessionToken()
      if (!existingToken) {
        if (!cancelled) setBootstrapping(false)
        return
      }

      try {
        const me = await clientApi.me()
        if (cancelled) return
        const currentEmail = String(me?.account?.email || '').trim().toLowerCase() || null
        setSignedInEmail(currentEmail)

        if (!nextInviteCode) {
          setBootstrapping(false)
          nav('/dashboard', { replace: true })
          return
        }

        try {
          const preview = await clientApi.invitePreview(nextInviteCode)
          if (cancelled) return
          setInvitePreview(preview)
          setBootstrapping(false)
          return
        } catch (e) {
          if (cancelled) return
          setInviteConflict(true)
          setError(e)
          setBootstrapping(false)
        }
      } catch {
        clearSession()
      }

      if (!cancelled) setBootstrapping(false)
    }

    init()
    return () => {
      cancelled = true
    }
  }, [nav])

  useEffect(() => {
    if (resendUntil <= Date.now()) {
      setNow(Date.now())
      return
    }
    const timer = window.setInterval(() => {
      const current = Date.now()
      setNow(current)
      if (current >= resendUntil) {
        window.clearInterval(timer)
      }
    }, 1000)
    return () => window.clearInterval(timer)
  }, [resendUntil])

  useEffect(() => {
    if (resendUntil > Date.now() && lastRequestedEmail) return
    try {
      localStorage.removeItem(resendStateStorageKey)
    } catch {
      // ignore
    }
  }, [lastRequestedEmail, resendUntil])

  async function start() {
    if (resendLocked) return
    setError(null)
    setInviteConflict(false)
    setBusyAction('sending')
    try {
      const r = await clientApi.authStart(email, inviteCode)
      setDevToken(r.dev_token || null)
      if (r.dev_token) setToken(r.dev_token)
      if (!r.sent && !r.dev_token) {
        throw new Error('Failed to send verification email. Please try again shortly.')
      }
      const nextUntil = Date.now() + resendCooldownMs
      const requestedEmail = normalizedEmail
      setLastRequestedEmail(requestedEmail)
      setResendUntil(nextUntil)
      try {
        localStorage.setItem(resendStateStorageKey, JSON.stringify({ email: requestedEmail, until: nextUntil }))
      } catch {
        // ignore
      }
    } catch (e) {
      setError(e)
    } finally {
      setBusyAction(null)
    }
  }

  async function verify() {
    setError(null)
    setInviteConflict(false)
    setBusyAction('verifying')
    try {
      const r = await clientApi.authVerify(email, token, inviteCode, orgName || null, null)
      setSessionToken(r.session_token)
      if (r.default_watchlist_id) {
        setDefaultWatchlistId(r.default_watchlist_id)
      }
      const activeOrgId = r.org?.org_id || r.account?.active_org_id || null
      if (activeOrgId) setActiveOrgId(String(activeOrgId))
      try {
        localStorage.removeItem(inviteStorageKey)
      } catch {
        // ignore
      }
      if (r.org_setup_required || r.onboarding_completed === false) nav('/onboarding')
      else nav('/dashboard')
    } catch (e) {
      setError(e)
    } finally {
      setBusyAction(null)
    }
  }

  async function acceptSignedInInvite() {
    if (!inviteCode) return
    setError(null)
    setInviteConflict(false)
    setBusyAction('joining')
    try {
      const accepted = await clientApi.inviteAccept(inviteCode)
      if (accepted.default_watchlist_id) {
        setDefaultWatchlistId(accepted.default_watchlist_id)
      }
      try {
        localStorage.removeItem(inviteStorageKey)
      } catch {
        // ignore
      }
      setInvitePreview(null)
      setInviteCode(null)
      if (accepted.org_setup_required || accepted.onboarding_completed === false) nav('/onboarding', { replace: true })
      else nav('/dashboard', { replace: true })
    } catch (e) {
      setInviteConflict(true)
      setError(e)
    } finally {
      setBusyAction(null)
    }
  }

  function dismissSignedInInvite() {
    try {
      localStorage.removeItem(inviteStorageKey)
    } catch {
      // ignore
    }
    setInviteCode(null)
    setInvitePreview(null)
    setError(null)
    nav('/dashboard', { replace: true })
  }

  let statusText: string | null = null
  let statusColor = 'var(--muted)'
  if (busyAction === 'joining') {
    statusText = `Joining invited organization as ${signedInEmail || 'your current account'}...`
  } else if (busyAction === 'sending') {
    statusText = 'Sending verification code...'
  } else if (busyAction === 'verifying') {
    statusText = 'Verifying code...'
  } else if (devToken) {
    statusColor = 'var(--good)'
    statusText = resendLocked
      ? `Verification code is ready below. You can request another in ${resendSeconds}s.`
      : 'Verification code is ready below. You can request another code now.'
  } else if (resendAppliesToCurrentEmail && lastRequestedEmail) {
    statusColor = 'var(--good)'
    statusText = resendLocked
      ? `Verification code sent to ${lastRequestedEmail}. Enter it below. You can resend in ${resendSeconds}s.`
      : `Verification code sent to ${lastRequestedEmail}. Enter it below, or resend if needed.`
  } else if (lastRequestedEmail) {
    statusText = `A code was last sent to ${lastRequestedEmail}.`
  }

  const sendButtonLabel = busyAction === 'sending'
    ? 'Sending...'
    : resendAppliesToCurrentEmail && resendLocked
      ? `Resend in ${resendSeconds}s`
      : lastRequestedEmail && resendAppliesToCurrentEmail
        ? 'Resend code'
        : 'Send code'

  return (
    <div className="container">
      <Topbar subtitle="Sign in" />

      <div className="grid">
        <div className="card">
          <h2>Sign in</h2>
          <div className="muted">Passwordless login — check your email for a verification code.</div>
          <div style={{ height: 10 }} />

          {bootstrapping ? (
            <div className="muted" style={{ fontSize: 12 }}>Checking your session...</div>
          ) : null}

          {!bootstrapping && inviteConflict && signedInEmail ? (
            <>
              <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
                You are already signed in as <span className="mono">{signedInEmail}</span>. This invite could not be accepted with the current account.
              </div>
              <div className="muted" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5 }}>
                Continue to your dashboard, or sign out and use the invited email address.
              </div>
              <div style={{ height: 12 }} />
              <div className="row">
                <button className="btn" onClick={() => nav('/dashboard', { replace: true })}>Continue to dashboard</button>
                <button
                  className="btn primary"
                  onClick={() => {
                    clearSession()
                    setSignedInEmail(null)
                    setInviteConflict(false)
                    setError(null)
                  }}
                >
                  Sign out and switch account
                </button>
              </div>
            </>
          ) : !bootstrapping ? (
            <>
              <div className="row">
                <input className="input" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <button className="btn primary" onClick={start} disabled={busy || !normalizedEmail || resendLocked}>{sendButtonLabel}</button>
              </div>

              {statusText ? (
                <div className="muted" style={{ marginTop: 10, fontSize: 12, color: statusColor }}>
                  {statusText}
                </div>
              ) : null}

              <div className="muted" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5 }}>
                Didn't get it? Check spam, promotions, and that your email address is correct.
              </div>

              {inviteCode ? (
                <div style={{ marginTop: 12 }}>
                  <div className="muted" style={{ fontSize: 12 }}>Organization name (optional)</div>
                  <div style={{ height: 6 }} />
                  <input className="input" placeholder="Acme Capital" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
                </div>
              ) : null}

              <div style={{ height: 12 }} />
              <div className="row">
                <input className="input mono" placeholder="6-digit code" value={token} onChange={(e) => setToken(e.target.value)} />
                <button className="btn" onClick={verify} disabled={busy || !normalizedEmail || !token}>Verify</button>
              </div>

              <div className="muted" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5 }}>
                {resendAppliesToCurrentEmail && resendLocked
                  ? `You can request a new code in ${resendSeconds}s.`
                  : 'If the code expires or never arrives, request a new one.'}
              </div>
            </>
          ) : null}
        </div>

        {error ? <ErrorBox error={error} /> : null}
      </div>

      {!bootstrapping && signedInEmail && invitePreview && !inviteConflict ? (
        <div className="modalOverlay" onClick={dismissSignedInInvite}>
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 800 }}>Accept Invite?</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  Signed in as {signedInEmail}
                </div>
              </div>
              <button className="btn" onClick={dismissSignedInInvite} disabled={busyAction === 'joining'}>
                Cancel
              </button>
            </div>

            <div className="muted" style={{ marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>
              {invitePreview?.org?.name
                ? `Join ${invitePreview.org.name}${invitePreview.role ? ` as ${invitePreview.role}` : ''}?`
                : invitePreview?.requires_new_org
                  ? 'Accept this invite to create and join a new organization with your current account?'
                  : `Accept this invite${invitePreview?.role ? ` as ${invitePreview.role}` : ''}?`}
            </div>

            {invitePreview?.org?.slug ? (
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                Org slug: <span className="mono">{invitePreview.org.slug}</span>
              </div>
            ) : null}

            {invitePreview?.email_hint ? (
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                Invite restriction: <span className="mono">{invitePreview.email_hint}</span>
              </div>
            ) : null}

            <div className="muted" style={{ marginTop: 10, fontSize: 12, lineHeight: 1.5 }}>
              Only accept this if you intended to join this organization.
            </div>

            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn" onClick={dismissSignedInInvite} disabled={busyAction === 'joining'}>
                Not now
              </button>
              <button className="btn primary" onClick={acceptSignedInInvite} disabled={busy}>
                {busyAction === 'joining' ? 'Joining...' : 'Join organization'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
